import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { buildOrderedTreeIndex, collectDescendants } from '../lib/wbsTree';
import { useColumnResize } from 'src/hooks/useColumnResize';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { formatProjectDateOnly } from 'src/utils/date';

export type SCurveWbsRow = {
    key: string;
    nodeId: string;
    parentNodeId: string | null;

    code: string | null;
    name: string;

    orderIndex: number | null;
    level: number | null;

    plannedStart: string | null; // YYYY-MM-DD
    plannedFinish: string | null; // YYYY-MM-DD
    weight: number | null; // 0~100
};

type Mode = 'QUICK' | 'PRECISE';
type StepUnit = 'DAY' | 'WEEK';

type OriginalMeta = {
    plannedStart: string | null;
    plannedFinish: string | null;
    weight: number | null;
};

type ActionState = {
    canShiftBack: boolean;
    canShiftFwd: boolean;
    canShrink: boolean;
    canExtend: boolean;
    canStartBack: boolean;
    canStartFwd: boolean;
};

type Props = {
    source: 'CURRENT' | 'DRAFT';
    rows: SCurveWbsRow[];
    editable: boolean;

    selectedKey: string | null;
    onSelect: (key: string) => void;

    onEditField: (
        rowKey: string,
        patch: Partial<Pick<SCurveWbsRow, 'plannedStart' | 'plannedFinish' | 'weight'>>,
    ) => void;

    onShift: (rowKey: string, dir: 'BACK' | 'FWD') => void; // -1d / +1d
    onExtendFinish: (rowKey: string, dir: 'SHRINK' | 'EXTEND') => void; // finish -1 / +1
    onExtendStart: (rowKey: string, dir: 'BACK' | 'FWD') => void; // start -1 / +1

    // 상단 전체 Reset (draft 전체)
    onReset?: () => void;
    canReset?: boolean;

    // original/action state (draft only)
    originalByKey?: Map<string, OriginalMeta>;
    actionStateByKey?: Map<string, ActionState>;
};

// -----------------------------
// util
// -----------------------------
function toTs(iso: string | null) {
    if (!iso) return NaN;
    const d = formatProjectDateOnly(iso, 'Asia/Seoul', '');
    if (!d) return NaN;
    const t = new Date(`${d}T00:00:00`).getTime();
    return Number.isFinite(t) ? t : NaN;
}

function fmtWeight(v: number | null) {
    if (v == null || !Number.isFinite(v)) return '—';
    return v.toFixed(v % 1 === 0 ? 0 : 2);
}

function isChanged(o: OriginalMeta | undefined, r: SCurveWbsRow) {
    if (!o) return false;
    return (
        (o.plannedStart ?? null) !== (r.plannedStart ?? null) ||
        (o.plannedFinish ?? null) !== (r.plannedFinish ?? null) ||
        (o.weight ?? null) !== (r.weight ?? null)
    );
}

export default function SCurveWbsList({
    source,
    rows,
    editable,
    selectedKey,
    onSelect,
    onEditField,
    onShift,
    onExtendFinish,
    onReset,
    canReset,
    originalByKey,
    actionStateByKey,
    onExtendStart,
}: Props) {
    const readOnly = source !== 'DRAFT' || !editable;
    const [mode, setMode] = useState<Mode>('QUICK');

    const [panelOpen, setPanelOpen] = useState(true);

    const [stepUnit, setStepUnit] = useState<StepUnit>('DAY');

    const stepDays = stepUnit === 'WEEK' ? 7 : 1;

    const shiftWithStep = useCallback(
        (rowKey: string, dir: 'BACK' | 'FWD') => {
            for (let i = 0; i < stepDays; i++) {
                onShift(rowKey, dir);
            }
        },
        [onShift, stepDays],
    );

    const extendFinishWithStep = useCallback(
        (rowKey: string, dir: 'SHRINK' | 'EXTEND') => {
            for (let i = 0; i < stepDays; i++) {
                onExtendFinish(rowKey, dir);
            }
        },
        [onExtendFinish, stepDays],
    );

    const extendStartWithStep = useCallback(
        (rowKey: string, dir: 'BACK' | 'FWD') => {
            for (let i = 0; i < stepDays; i++) {
                onExtendStart(rowKey, dir);
            }
        },
        [onExtendStart, stepDays],
    );

    // const { ordered, childrenMap } = useMemo(() => buildTreeIndex(rows), [rows]);
    const { ordered, childrenMap } = useMemo(
        () =>
            buildOrderedTreeIndex(rows, {
                getKey: (r) => r.key,
                getNodeId: (r) => r.nodeId,
                getParentNodeId: (r) => r.parentNodeId,
                getOrderIndex: (r) => r.orderIndex,
            }),
        [rows],
    );

    // ✅ Column Resize
    const WBS_COL_SS_KEY = 'scurve:wbsColWidth';
    const initWbsWidth = useMemo(() => {
        const raw = sessionStorage.getItem(WBS_COL_SS_KEY);
        const n = raw ? Number(raw) : NaN;

        return Number.isFinite(n) ? n : 340; // 기본값
    }, []);
    const wbsCol = useColumnResize('scurve-wbs', initWbsWidth, 180, 720, { edge: 'left' });

    useEffect(() => {
        sessionStorage.setItem(WBS_COL_SS_KEY, String(wbsCol.width));
    }, [wbsCol.width]);

    useEffect(() => {
        if (selectedKey) setPanelOpen(true);
    }, [selectedKey]);

    // ✅ expanded: true면 펼침 (WbsList.tsx 방식과 동일 컨셉)
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>());

    const hasChildren = useCallback((k: string) => (childrenMap.get(k)?.length ?? 0) > 0, [childrenMap]);
    const isExpanded = useCallback((k: string) => expanded.has(k), [expanded]);

    const toggleExpand = useCallback((k: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        const next = new Set<string>();
        for (const r of ordered) if (hasChildren(r.key)) next.add(r.key);
        setExpanded(next);
    }, [ordered, hasChildren]);

    const collapseAll = useCallback(() => {
        setExpanded(new Set());
    }, []);

    // ✅ visible rows (expanded 기준)
    const visibleRows = useMemo(() => {
        if (!ordered.length) return ordered;

        // 접힌(미확장) 노드의 자손은 숨김
        const hidden = new Set<string>();
        for (const r of ordered) {
            if (!hasChildren(r.key)) continue;
            if (isExpanded(r.key)) continue;
            for (const d of collectDescendants(r.key, childrenMap)) hidden.add(d);
        }
        return ordered.filter((r) => !hidden.has(r.key));
    }, [ordered, childrenMap, hasChildren, isExpanded]);

    // bar용 global
    const { globalMin, globalMax } = useMemo(() => {
        let minTs = Number.POSITIVE_INFINITY;
        let maxTs = Number.NEGATIVE_INFINITY;

        for (const r of rows) {
            const s = toTs(r.plannedStart);
            const f = toTs(r.plannedFinish);
            if (Number.isFinite(s)) minTs = Math.min(minTs, s);
            if (Number.isFinite(f)) maxTs = Math.max(maxTs, f);
        }

        if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs <= minTs) {
            return { globalMin: NaN, globalMax: NaN };
        }
        return { globalMin: minTs, globalMax: maxTs };
    }, [rows]);

    const selected = rows.find((r) => r.key === selectedKey) ?? null;
    const orig = selected ? originalByKey?.get(selected.key) : undefined;
    const action = selected ? actionStateByKey?.get(selected.key) : undefined;

    const selectedChanged = selected ? isChanged(orig, selected) : false;

    // ✅ 선택 노드(+자손) Reset
    const canResetSelected = useMemo(() => {
        if (source !== 'DRAFT') return false;
        if (readOnly) return false;
        if (!selected) return false;
        if (!originalByKey) return false;

        // 선택/자손 중 하나라도 변경이면 true
        const keys = [selected.key, ...collectDescendants(selected.key, childrenMap)];
        return keys.some((k) => {
            const r = rows.find((x) => x.key === k);
            const o = originalByKey.get(k);
            return r && isChanged(o, r);
        });
    }, [source, readOnly, selected, originalByKey, rows, childrenMap]);

    const resetSelectedSubtree = useCallback(() => {
        if (!selected) return;
        if (!originalByKey) return;

        const keys = [selected.key, ...collectDescendants(selected.key, childrenMap)];
        for (const k of keys) {
            const o = originalByKey.get(k);
            if (!o) continue;
            onEditField(k, {
                plannedStart: o.plannedStart ?? null,
                plannedFinish: o.plannedFinish ?? null,
                weight: o.weight ?? null,
            });
        }
    }, [selected, originalByKey, childrenMap, onEditField]);

    return (
        <div className="h-full min-h-0 flex gap-4">
            {/* ===================== */}
            {/* LEFT: LIST (7)        */}
            {/* ===================== */}
            <div className="flex-[8] overflow-hidden rounded-2xl border border-slate-200 bg-white flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-12">
                        <div className="text-sm font-semibold text-slate-800">WBS List</div>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                className="h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
                                onClick={expandAll}
                                title="전체 펼치기 / Expand All"
                            >
                                전체 펼치기
                            </button>

                            <button
                                type="button"
                                className="h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
                                onClick={collapseAll}
                                title="전체 접기 / Collapse All"
                            >
                                전체 접기
                            </button>
                        </div>
                    </div>

                    <div className="text-xs text-slate-500">
                        {source === 'DRAFT'
                            ? readOnly
                                ? 'Draft (read-only)'
                                : 'Draft (editable)'
                            : 'Published (read-only)'}
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-auto pb-6">
                    <table className="min-w-[1200px] w-full text-sm table-fixed">
                        <colgroup>
                            <col style={{ width: wbsCol.width }} />
                            <col style={{ width: 90 }} />
                            <col style={{ width: 140 }} />
                            <col style={{ width: 140 }} />
                            <col />
                        </colgroup>
                        <thead className="sticky top-0 bg-slate-50 z-10">
                            <tr className="text-slate-600">
                                {/* WBS 리사이즈 핸들 */}
                                <th className="px-0 py-2 text-left">
                                    <div className="flex items-center border-r" style={{ width: wbsCol.width }}>
                                        <div className="px-10">Code / Title</div>
                                        <div
                                            onMouseDown={wbsCol.onMouseDown}
                                            className="ml-auto w-1 self-stretch cursor-col-resize hover:bg-gray-600"
                                            style={{ marginRight: -4 }}
                                            title="열 너비 조절"
                                        />
                                    </div>
                                </th>
                                <th className="text-left px-3 py-2">Weight</th>
                                <th className="text-left px-3 py-2">Start</th>
                                <th className="text-left px-3 py-2">Finish</th>
                                <th className="text-left px-3 py-2">Schedule</th>
                            </tr>
                        </thead>

                        <tbody>
                            {visibleRows.map((r) => {
                                const o = originalByKey?.get(r.key);
                                const rowChanged = isChanged(o, r);

                                const s = toTs(r.plannedStart);
                                const f = toTs(r.plannedFinish);
                                const denom = globalMax - globalMin;

                                const leftPct =
                                    Number.isFinite(s) &&
                                    Number.isFinite(globalMin) &&
                                    Number.isFinite(denom) &&
                                    denom > 0
                                        ? ((s - globalMin) / denom) * 100
                                        : 0;

                                const widthPct =
                                    Number.isFinite(s) && Number.isFinite(f) && Number.isFinite(denom) && denom > 0
                                        ? Math.max(1, ((f - s) / denom) * 100)
                                        : 0;

                                const canToggle = hasChildren(r.key);
                                const exp = isExpanded(r.key);

                                const showOrigWeight = o?.weight != null && o.weight !== r.weight;
                                const showOrigStart = o?.plannedStart && o.plannedStart !== r.plannedStart;
                                const showOrigFinish = o?.plannedFinish && o.plannedFinish !== r.plannedFinish;

                                return (
                                    <tr
                                        key={r.key}
                                        onClick={() => onSelect(r.key)}
                                        className={[
                                            'border-t border-slate-100 cursor-pointer',
                                            selectedKey === r.key ? 'bg-blue-50' : 'hover:bg-slate-50',
                                        ].join(' ')}
                                    >
                                        {/* WBS */}
                                        <td className="px-3 py-2 align-top">
                                            <div className="flex items-center gap-2" style={{ width: wbsCol.width }}>
                                                {/* Changed 표시 */}
                                                {rowChanged ? (
                                                    <span
                                                        className="inline-block w-2 h-2 rounded-full bg-amber-500"
                                                        title="변경됨"
                                                        aria-label="변경됨"
                                                    />
                                                ) : (
                                                    <span className="inline-block w-2 h-2 rounded-full" />
                                                )}
                                                {/* indent */}
                                                <div
                                                    className="pt-1 shrink-0 "
                                                    style={{ width: Math.max(0, (r.level ? r.level - 1 : 0) * 16) }}
                                                />

                                                {/* ✅ WbsList 스타일 SVG 토글 */}
                                                {canToggle ? (
                                                    <button
                                                        type="button"
                                                        className="mt-0.5 mr-1 inline-flex w-5 h-5 items-center justify-center rounded hover:bg-slate-100"
                                                        title={exp ? '접기' : '펼치기'}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleExpand(r.key);
                                                        }}
                                                    >
                                                        <svg
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 24 24"
                                                            className={`transition-transform ${exp ? 'rotate-90' : ''}`}
                                                        >
                                                            <path d="M8 5l8 7-8 7V5z" fill="currentColor" />
                                                        </svg>
                                                    </button>
                                                ) : (
                                                    <span className="w-5 h-5 mr-1" />
                                                )}
                                                {/* WBS 코드 + 이름 */}
                                                <div className="relative min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="truncate font-medium overflow-hidden whitespace-nowrap text-ellipsis text-left text-slate-800">
                                                            {r.code ? (
                                                                <span className="text-slate-500 text-xs mr-2">
                                                                    {r.code}
                                                                </span>
                                                            ) : null}
                                                            {r.name}
                                                        </div>
                                                        {/* (선택) 오른쪽 fade 처리: WbsList 스타일 */}
                                                        <span
                                                            aria-hidden
                                                            className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Weight */}
                                        <td className="px-3 py-2 align-top">
                                            <div className="font-mono">{fmtWeight(r.weight)}</div>
                                            {showOrigWeight && (
                                                <div className="mt-0.5 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{fmtWeight(o!.weight)}</span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Start */}
                                        <td className="px-3 py-2 align-top">
                                            <div className="font-mono">{r.plannedStart ?? '—'}</div>
                                            {showOrigStart && (
                                                <div className="mt-0.5 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{o!.plannedStart}</span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Finish */}
                                        <td className="px-3 py-2 align-top">
                                            <div className="font-mono">{r.plannedFinish ?? '—'}</div>
                                            {showOrigFinish && (
                                                <div className="mt-0.5 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{o!.plannedFinish}</span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Bar */}
                                        <td className="px-3 py-2 align-top">
                                            <div className="h-6 w-full rounded bg-slate-100 relative overflow-hidden">
                                                <div
                                                    className={[
                                                        'absolute top-1 h-4 rounded',
                                                        selectedKey === r.key ? 'bg-blue-600/80' : 'bg-blue-500/70',
                                                    ].join(' ')}
                                                    style={{
                                                        left: `${Math.max(0, Math.min(100, leftPct))}%`,
                                                        width: `${Math.max(0, Math.min(100, widthPct))}%`,
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {!visibleRows.length && (
                                <tr>
                                    <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                                        표시할 WBS가 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ===================== */}
            {/* RIGHT: PANEL (3)      */}
            {/* ===================== */}
            <div /*className="flex-[2] overflow-hidden rounded-2xl border border-slate-200 bg-white flex flex-col min-h-0"*/
                className={[
                    'overflow-hidden rounded-2xl border border-slate-200 bg-white flex flex-col min-h-0',
                    'transition-[width] duration-200 ease-out',
                    panelOpen ? 'w-[380px]' : 'w-20',
                ].join(' ')}
            >
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                    <button
                        type="button"
                        className="//h-8 //w-8 //rounded-lg //border //border-slate-200 //text-xs //text-slate-700 hover:bg-slate-50"
                        onClick={() => setPanelOpen((v) => !v)}
                        title={panelOpen ? '접기' : '펼치기'}
                    >
                        {panelOpen ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronLeftIcon className="w-4 h-4" />}
                    </button>
                    {panelOpen && (
                        <>
                            <div className="text-sm font-semibold text-slate-800">WBS Adjustment</div>
                            <div className="flex items-center gap-2">
                                {/* ✅ 전체 Reset (draft 전체) */}
                                {source === 'DRAFT' && (
                                    <button
                                        type="button"
                                        className="h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={onReset}
                                        disabled={readOnly || !canReset}
                                        title={readOnly ? '편집 권한이 없습니다' : '로드 시점 Draft로 복귀'}
                                    >
                                        Reset All
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {panelOpen && (
                    <>
                        {!selected && <div className="p-6 text-sm text-slate-500">왼쪽에서 WBS를 선택하세요.</div>}

                        {selected && (
                            <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-slate-800 truncate">
                                            {selected.name}
                                        </div>
                                        {source === 'DRAFT' && selectedChanged && (
                                            <div className="mt-1 text-xs text-amber-600">변경됨</div>
                                        )}
                                    </div>

                                    {/* ✅ 선택 항목(서브트리 포함) Reset */}
                                    {source === 'DRAFT' && (
                                        <button
                                            type="button"
                                            className="shrink-0 h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                            onClick={resetSelectedSubtree}
                                            disabled={readOnly || !canResetSelected}
                                            title={
                                                readOnly
                                                    ? '편집 권한이 없습니다.'
                                                    : '선택한 WBS + 자손을 원래값으로 복귀'
                                            }
                                        >
                                            Reset Selected
                                        </button>
                                    )}
                                </div>

                                {/* mode */}
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setMode('QUICK')}
                                        className={[
                                            'h-8 px-3 rounded-lg border text-xs',
                                            mode === 'QUICK'
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
                                        ].join(' ')}
                                    >
                                        Quick
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMode('PRECISE')}
                                        className={[
                                            'h-8 px-3 rounded-lg border text-xs',
                                            mode === 'PRECISE'
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-slate-700 border-slate-200 //hover:bg-slate-50',
                                        ].join(' ')}
                                        disabled // 일단 검증이 안되어서 비활성화 함
                                    >
                                        Precise
                                    </button>
                                </div>

                                {/* quick */}
                                {mode === 'QUICK' && (
                                    <div className="mt-2 //space-y-2">
                                        {/* <div className="text-xs text-slate-500">Applies to subtree</div> */}
                                        {/* Day / Week 선택 */}
                                        <div className="mt-2 flex items-center gap-2">
                                            <span className="text-xs text-slate-500">Step</span>

                                            <button
                                                type="button"
                                                className={[
                                                    'h-7 px-2 rounded-md border text-xs',
                                                    stepUnit === 'DAY'
                                                        ? 'border-slate-900 text-slate-900'
                                                        : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                                                ].join(' ')}
                                                onClick={() => setStepUnit('DAY')}
                                            >
                                                Day
                                            </button>

                                            <button
                                                type="button"
                                                className={[
                                                    'h-7 px-2 rounded-md border text-xs',
                                                    stepUnit === 'WEEK'
                                                        ? 'border-slate-900 text-slate-900'
                                                        : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                                                ].join(' ')}
                                                onClick={() => setStepUnit('WEEK')}
                                            >
                                                Week
                                            </button>
                                        </div>

                                        <div className="mt-2 flex flex-wrap gap-5">
                                            {/* ✅ Start -1 / +1 추가 */}
                                            <div>
                                                <div className="text-xs">Start</div>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={!editable || !action?.canStartBack}
                                                        onClick={() =>
                                                            selected && extendStartWithStep(selected.key, 'BACK')
                                                        }
                                                        title="Start -1 day (더 빨리 시작)"
                                                    >
                                                        -1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={!editable || !action?.canStartFwd}
                                                        onClick={() =>
                                                            selected && extendStartWithStep(selected.key, 'FWD')
                                                        }
                                                        title="Start +1 day (더 늦게 시작)"
                                                    >
                                                        +1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* ✅ Shift -1 / +1 추가 */}
                                            <div>
                                                <div className="text-xs">Shift</div>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={readOnly || !action?.canShiftBack}
                                                        onClick={() => selected && shiftWithStep(selected.key, 'BACK')}
                                                    >
                                                        −1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={readOnly || !action?.canShiftFwd}
                                                        onClick={() => selected && shiftWithStep(selected.key, 'FWD')}
                                                    >
                                                        +1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* ✅ Finish -1 / +1 추가 */}
                                            <div>
                                                <div className="text-xs">Finish</div>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={readOnly || !action?.canShrink}
                                                        onClick={() =>
                                                            selected && extendFinishWithStep(selected.key, 'SHRINK')
                                                        }
                                                    >
                                                        −1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="px-2 py-1 rounded border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                        disabled={readOnly || !action?.canExtend}
                                                        onClick={() =>
                                                            selected && extendFinishWithStep(selected.key, 'EXTEND')
                                                        }
                                                    >
                                                        +1 {stepUnit === 'DAY' ? 'day' : 'week'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* precise */}
                                {mode === 'PRECISE' && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-slate-600 mb-1">Weight (%)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                max={100}
                                                step={0.01}
                                                value={selected.weight ?? ''}
                                                disabled={readOnly}
                                                onChange={(e) =>
                                                    onEditField(selected.key, {
                                                        weight: e.target.value === '' ? null : Number(e.target.value),
                                                    })
                                                }
                                                className={[
                                                    'h-10 w-full rounded-lg border px-3 text-sm',
                                                    readOnly
                                                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                                                        : 'bg-white border-slate-200',
                                                ].join(' ')}
                                            />
                                            {/* ✅ 원래값 표시 복원 */}
                                            {orig?.weight != null && orig.weight !== selected.weight && (
                                                <div className="mt-1 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{fmtWeight(orig.weight)}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-slate-600 mb-1">Start</label>
                                            <input
                                                type="date"
                                                value={selected.plannedStart ?? ''}
                                                disabled={readOnly}
                                                onChange={(e) =>
                                                    onEditField(selected.key, { plannedStart: e.target.value || null })
                                                }
                                                className={[
                                                    'h-10 w-full rounded-lg border px-3 text-sm',
                                                    readOnly
                                                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                                                        : 'bg-white border-slate-200',
                                                ].join(' ')}
                                            />
                                            {/* ✅ 원래값 표시 복원 */}
                                            {orig?.plannedStart && orig.plannedStart !== selected.plannedStart && (
                                                <div className="mt-1 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{orig.plannedStart}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-slate-600 mb-1">Finish</label>
                                            <input
                                                type="date"
                                                value={selected.plannedFinish ?? ''}
                                                disabled={readOnly}
                                                onChange={(e) =>
                                                    onEditField(selected.key, { plannedFinish: e.target.value || null })
                                                }
                                                className={[
                                                    'h-10 w-full rounded-lg border px-3 text-sm',
                                                    readOnly
                                                        ? 'bg-slate-50 border-slate-200 text-slate-500'
                                                        : 'bg-white border-slate-200',
                                                ].join(' ')}
                                            />
                                            {/* ✅ 원래값 표시 복원 */}
                                            {orig?.plannedFinish && orig.plannedFinish !== selected.plannedFinish && (
                                                <div className="mt-1 text-[11px] text-slate-500">
                                                    원래: <span className="font-mono">{orig.plannedFinish}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="pt-2 border-t border-slate-100">
                                            <div className="text-xs text-slate-500 mb-2">Quick moves (subtree)</div>
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    className="h-9 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    disabled={readOnly || !action?.canShiftBack}
                                                    onClick={() => onShift(selected.key, 'BACK')}
                                                >
                                                    −1 day
                                                </button>
                                                <button
                                                    type="button"
                                                    className="h-9 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    disabled={readOnly || !action?.canShiftFwd}
                                                    onClick={() => onShift(selected.key, 'FWD')}
                                                >
                                                    +1 day
                                                </button>
                                                <button
                                                    type="button"
                                                    className="h-9 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    disabled={readOnly || !action?.canShrink}
                                                    onClick={() => onExtendFinish(selected.key, 'SHRINK')}
                                                >
                                                    Finish −1
                                                </button>
                                                <button
                                                    type="button"
                                                    className="h-9 px-3 rounded-lg border border-slate-200 text-xs hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    disabled={readOnly || !action?.canExtend}
                                                    onClick={() => onExtendFinish(selected.key, 'EXTEND')}
                                                >
                                                    Finish +1
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
